import { useState } from 'react';
import { useLang } from '@/contexts/LanguageContext';
import { useGravityToast } from '@/components/effects/GravityToast';
import api from '@/utils/api';
import ScrollReveal from '@/components/effects/ScrollReveal';
import { Send, Mail, Instagram, Twitter, Facebook, Youtube } from 'lucide-react';

export default function Contact() {
  const { t } = useLang();
  const toast = useGravityToast();
  const [form, setForm] = useState({ name: '', email: '', subject: 'general', message: '' });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post('/contact', form);
      toast.success(t('contact.sent'));
      setForm({ name: '', email: '', subject: 'general', message: '' });
    } catch {
      toast.error('Failed to send message');
    }
    setSending(false);
  };

  const subjects = t('contact.subjects');

  return (
    <div data-testid="contact-page" className="min-h-screen py-20 md:py-32 px-4">
      <div className="max-w-4xl mx-auto">
        <ScrollReveal>
          <h1 className="text-4xl sm:text-5xl font-display text-heading mb-3" data-testid="contact-title">{t('contact.title')}</h1>
          <p className="text-text-muted font-body mb-12">{t('contact.subtitle')}</p>
        </ScrollReveal>

        <div className="grid md:grid-cols-2 gap-12">
          <ScrollReveal>
            <div className="space-y-8">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <Mail size={20} className="text-sacred-gold" />
                  <span className="text-deep-brown font-body">contact@tariqwithin.com</span>
                </div>
              </div>
              <div>
                <p className="text-sacred-gold text-sm font-body uppercase tracking-wider mb-4">Follow @tariqwithin</p>
                <div className="flex gap-4">
                  {[
                    { icon: Instagram, href: 'https://instagram.com/tariqwithin', label: 'Instagram' },
                    { icon: Twitter, href: 'https://x.com/tariqwithin', label: 'X' },
                    { icon: Facebook, href: 'https://facebook.com/tariqwithin', label: 'Facebook' },
                    { icon: Youtube, href: 'https://youtube.com/@tariqwithin', label: 'YouTube' },
                  ].map(s => (
                    <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full glass-card flex items-center justify-center text-dust-gray hover:text-sacred-gold transition-colors" data-testid={`contact-social-${s.label.toLowerCase()}`}>
                      <s.icon size={18} />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
              <input type="text" placeholder={t('contact.name')} required value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="w-full px-4 py-3 rounded-xl glass-input font-body text-sm" data-testid="contact-form-name" />
              <input type="email" placeholder={t('contact.email')} required value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} className="w-full px-4 py-3 rounded-xl glass-input font-body text-sm" data-testid="contact-form-email" />
              <select value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))} className="w-full px-4 py-3 rounded-xl glass-input font-body text-sm bg-transparent" data-testid="contact-form-subject">
                {typeof subjects === 'object' && Object.entries(subjects).map(([k, v]) => (
                  <option key={k} value={k} className="bg-white text-deep-brown">{v}</option>
                ))}
              </select>
              <textarea placeholder={t('contact.message')} required rows={5} value={form.message} onChange={e => setForm(p => ({ ...p, message: e.target.value }))} className="w-full px-4 py-3 rounded-xl glass-input font-body text-sm resize-none" data-testid="contact-form-message" />
              <button type="submit" disabled={sending} className="gold-button px-6 py-3 rounded-full font-body text-sm flex items-center gap-2 disabled:opacity-50" data-testid="contact-form-submit">
                <Send size={16} /> {sending ? '...' : t('contact.send')}
              </button>
            </form>
          </ScrollReveal>
        </div>
      </div>
    </div>
  );
}
